import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

/**
 * Axios instance compartilhada por toda a aplicacao.
 * - Token de acesso injetado via callback (memoria, nunca localStorage).
 * - Em 401 tenta refresh (cookie HttpOnly) e reexecuta a request 1x.
 * - Se refresh falhar, dispara onUnauthorized (logout).
 */

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

let getAccessToken: () => string | undefined = () => undefined;
let setAccessToken: (token: string) => void = () => undefined;
let onUnauthorized: () => void = () => undefined;

export function configureApiClient(opts: {
  getAccessToken: () => string | undefined;
  setAccessToken: (token: string) => void;
  onUnauthorized: () => void;
}): void {
  getAccessToken = opts.getAccessToken;
  setAccessToken = opts.setAccessToken;
  onUnauthorized = opts.onUnauthorized;
}

export const apiClient = axios.create({
  baseURL: `${baseURL}/api/v1`,
  withCredentials: true,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Estado para evitar refresh em paralelo
let refreshPromise: Promise<string | null> | null = null;

async function tentarRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<{ sucesso: true; dados: { accessToken: string } }>(
        `${baseURL}/api/v1/auth/refresh`,
        {},
        { withCredentials: true, timeout: 10_000 },
      )
      .then((resp) => resp.data.dados.accessToken)
      .catch(() => null)
      .finally(() => {
        // limpa para proxima 401 isolada poder tentar de novo
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const isAuthRoute = original?.url?.includes('/auth/');

    // Tenta refresh apenas em 401 fora das rotas de auth e so 1x por request
    if (status === 401 && original && !original._retry && !isAuthRoute) {
      original._retry = true;
      const novoToken = await tentarRefresh();
      if (novoToken) {
        setAccessToken(novoToken);
        original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${novoToken}` };
        return apiClient.request(original);
      }
      onUnauthorized();
    }

    return Promise.reject(error);
  },
);
